// @ts-check
/**
 * <oc-cart-upsell> — the cart-drawer cross-sell strip that sits under the last line item.
 *
 * Two jobs:
 *
 * 1. "+" on a card POSTs that variant to /cart/add.js and dispatches CartAddEvent.
 *    CartAddEvent bubbles and carries no `sections`, so cart-items-component's
 *    #handleCartUpdate falls through to sectionRenderer.renderSection() — a fresh
 *    server render of the drawer. That is what recomputes the offer list (the product
 *    just added is now in the cart, so it drops out). Same contract the PDP's
 *    <oc-upsell> relies on.
 *
 * 2. When Liquid found no `custom.upsell_in_pdp` offers, the element ships hidden with
 *    a `data-recs-url` and pulls Shopify recommendations for the last cart item, trying
 *    the curated `complementary` intent first and falling back to `related`.
 *
 * The drawer re-render may either replace this element (connectedCallback runs again) or
 * morph it in place (it does not). Both are covered: we reload on connect AND on the
 * document-level cart-update event, guarded so only one fetch is ever in flight.
 */

import { CartAddEvent } from '@theme/events';
import { requestIdleCallback, cartSectionIds, parseCartItemCount } from '@theme/utilities';

const RECS_SECTION_ID = 'oc-cart-upsell-recs';
const RECS_LIMIT = '8';
/** Curated pairings first; `related` is the algorithmic safety net that almost always returns rows. */
const RECS_INTENTS = ['complementary', 'related'];

class OcCartUpsell extends HTMLElement {
  /** @type {HTMLElement | null} */
  listEl = null;
  /** @type {HTMLElement | null} */
  errorEl = null;
  /** @type {boolean} */
  loading = false;
  /** Anchor product id whose intents all came back empty. Suppresses retry storms. @type {string | null} */
  #exhaustedFor = null;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#cacheRefs();

    this.addEventListener('click', this.#handleClick);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);

    /*
     * sections/header.liquid lazily re-renders this whole section after page load
     * (assets/section-hydration.js → renderSection with mode:'hydration'). That morph
     * keeps THIS element but restores its server markup: `hidden` comes back and the
     * list is emptied — and because the element is preserved rather than replaced,
     * connectedCallback never runs again. Any recommendations fetched before hydration
     * are therefore silently discarded, leaving the block invisible until the next cart
     * change. Watching our own subtree heals that, and any other morph, generically.
     */
    this.#observer = new MutationObserver(this.#handleMutation);
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });

    // Hydration is itself queued on an idle callback, so queuing behind it usually means
    // one fetch instead of two. The observer covers us if the order ever flips.
    requestIdleCallback(() => this.#maybeLoadRecommendations());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #cacheRefs() {
    this.listEl = this.querySelector('[data-upsell-list]');
    this.errorEl = this.querySelector('[data-upsell-error]');
  }

  /**
   * Something rewrote our DOM (hydration, a cart morph, or our own injection). Re-resolve
   * refs and reload if we were emptied. Our own writes land here too, but they leave cards
   * behind, so #maybeLoadRecommendations short-circuits — no loop.
   */
  #handleMutation = () => {
    this.#cacheRefs();
    this.#maybeLoadRecommendations();
  };

  /** The drawer re-renders on cart change; wait a frame for the morph, then re-evaluate. */
  #handleCartUpdate = () => {
    // The cart changed, so the anchor product may have too — a previously empty sweep
    // says nothing about the new one.
    this.#exhaustedFor = null;
    requestAnimationFrame(() => {
      this.#cacheRefs();
      this.#maybeLoadRecommendations();
    });
  };

  /** @returns {boolean} whether the strip currently holds at least one offer */
  #hasCards() {
    return Boolean(this.listEl?.querySelector('[data-upsell-add]'));
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-upsell-add]'));
    if (!button || button.disabled) return;
    this.#addToCart(button);
  };

  #maybeLoadRecommendations() {
    const { recsUrl, productId } = this.dataset;
    // Metafield upsells were rendered server-side: no recs-url, nothing to fetch.
    if (!recsUrl || !productId) return;
    if (this.loading || this.#hasCards()) return;
    // A previous sweep found nothing for this anchor. Without this, every mutation the
    // observer sees would re-run both intents against an endpoint we know is empty.
    if (this.#exhaustedFor === productId) return;

    this.loading = true;
    this.#loadRecommendations(recsUrl, productId)
      .then((found) => {
        if (!found) this.#exhaustedFor = productId;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  /**
   * @param {string} recsUrl
   * @param {string} productId
   * @returns {Promise<boolean>} whether any offer was rendered
   */
  async #loadRecommendations(recsUrl, productId) {
    for (const intent of RECS_INTENTS) {
      const url = new URL(recsUrl, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', RECS_LIMIT);
      url.searchParams.set('intent', intent);
      url.searchParams.set('section_id', RECS_SECTION_ID);

      try {
        const response = await fetch(url.toString());
        if (!response.ok) continue;

        const markup = await response.text();
        const freshList = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-upsell-list]');
        // An intent with no configured pairings renders an empty section — try the next one.
        if (!freshList || !freshList.querySelector('[data-upsell-add]')) continue;

        if (this.listEl) {
          this.listEl.replaceWith(freshList);
        } else {
          this.appendChild(freshList);
        }
        this.listEl = /** @type {HTMLElement} */ (freshList);

        const heading = this.querySelector('[data-upsell-heading]');
        if (heading && this.dataset.headingRecs) heading.textContent = this.dataset.headingRecs;

        this.hidden = false;
        return true;
      } catch (error) {
        console.error('[oc-cart-upsell] recommendations fetch failed', error);
      }
    }
    // Nothing to offer — the block stays hidden, which is the correct empty state.
    return false;
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const id = Number(button.dataset.variantId);
    if (!Number.isFinite(id) || id <= 0) return;

    this.#hideError();
    button.disabled = true;

    try {
      // Asking for the cart sections up front is what keeps this to a single request: the drawer
      // this button lives in morphs from `payload.sections`, instead of cart-items-component
      // re-fetching the section because the response had nothing to morph.
      const sections = cartSectionIds();

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], ...(sections ? { sections } : {}) }),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        this.#showError(payload?.description || payload?.message || 'Could not add this item.');
        button.disabled = false;
        return;
      }

      button.dataset.added = 'true';

      // Straight to the drawer. The item_count screen readers get is in the section HTML, so the
      // `GET /cart.js` this used to await bought nothing but a round trip.
      const itemCount = parseCartItemCount(payload?.sections);

      this.dispatchEvent(
        new CartAddEvent(itemCount === undefined ? undefined : { item_count: itemCount }, this.id || 'oc-cart-upsell', {
          source: 'oc-cart-upsell',
          itemCount: 1,
          variantId: String(id),
          sections: payload?.sections,
        })
      );
    } catch (error) {
      console.error('[oc-cart-upsell] add to cart failed', error);
      this.#showError('Something went wrong. Please try again.');
      button.disabled = false;
    }
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

if (!customElements.get('oc-cart-upsell')) customElements.define('oc-cart-upsell', OcCartUpsell);
